import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import useApi from "../../hooks/useApi";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { PasswordSettingsDto } from "../../types/User";
import { getPasswordSettings } from "../../services/userService";
import { PASSWORDSYSTEMSETTINGS } from "../../data/constants";
import PopoverWithArrow from "../../components/ui/PopoverWithArrow";
import { HelpOutline } from "@mui/icons-material";
import { List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { POPOVERMESSAGES, USERSIGNUPMESSAGES } from "../../constants/messages";

interface PasswordComponentProps {
  onPasswordInputChange: (password: string) => void;
  onConfirmPasswordInputChange: (password: string) => void;
  userFirstName: string;
  userLastName: string;
  userName: string;
  title?: string;
}
type PasswordComplexityRequirements = {
  U?: number; // Minimum number of uppercase letters
  L?: number; // Minimum number of lowercase letters
  N?: number; // Minimum number of numbers
  S?: number; // Minimum number of special characters
  Length?: number; // Minimum password length
  UserDetailsContains?: boolean; // Whether user details are in the password
  BlackList?: boolean; // Whether the password is in a blacklist
};

type PasswordComplexityRequirementsResult = {
  U?: boolean;
  L?: boolean;
  N?: boolean;
  S?: boolean;
  Length?: boolean;
  UserDetailsContains?: boolean;
  BlackList?: boolean;
  ProhibitedChars?: boolean;
};
export interface PasswordComponentRef {
  childComponetCall: () => boolean;
  reset?: () => void;
}

const PasswordComponent = forwardRef<PasswordComponentRef, PasswordComponentProps>(
  (
    {
      onPasswordInputChange,
      onConfirmPasswordInputChange,
      userFirstName,
      userLastName,
      userName,
      title,
    },
    ref
  ) => {
    PasswordComponent.displayName = "PasswordComponent";
    const [validationErrors, setValidationErrors] = useState({
      passwordError: false,
      confirmPasswordError: false,
      passwordErrorMessage: "",
      confirmPasswordErrorMessage: "",
    });
    const [passwordSettings, setPasswordSettings] = useState({
      blacklist: "",
      blacklistArray: [],
      complexityRequirements: "",
      prohibitedChars: "",
      length: 8,
    });
    const [passwordData, setPasswordData] = useState({
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      validationResults: {} as PasswordComplexityRequirementsResult,
    });
    const updateState = <T extends Record<string, unknown>>(
      setState: React.Dispatch<React.SetStateAction<T>>
    ) => {
      return (newState: Partial<T>) => {
        setState((prevState) => ({
          ...prevState,
          ...newState,
        }));
      };
    };
    const updateValidationErrorsState = updateState<typeof validationErrors>(setValidationErrors);
    const updatePasswordSettingsState = updateState<typeof passwordSettings>(setPasswordSettings);
    const updatePasswordDataState = updateState<typeof passwordData>(setPasswordData);
    const paswordRef = useRef<HTMLInputElement>(null);
    const confirmPaswordRef = useRef<HTMLInputElement>(null);
    const { executeApi } = useApi();
    useImperativeHandle(ref, () => ({
      childComponetCall: () => {
        return validateBothPassword(passwordData.password, passwordData.confirmPassword);
      },
      reset: () => {
        setPasswordData({
          password: "",
          confirmPassword: "",
          showPassword: false,
          showConfirmPassword: false,
          validationResults: {} as PasswordComplexityRequirementsResult,
        });
        setValidationErrors({
          passwordError: false,
          confirmPasswordError: false,
          passwordErrorMessage: "",
          confirmPasswordErrorMessage: "",
        });
      },
    }));
    useEffect(() => {
      const fetchPasswordSettings = async () => {
        const settings = await executeApi<string, PasswordSettingsDto>(getPasswordSettings, "");
        if (settings) {
          const blacklist = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PASSWORD_BLACKLIST
          )?.value;
          const complexityRequirements = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PASSWORD_COMPLEXITY_REQUIREMENTS
          )?.value;
          const length = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PASSWORD_LENGTH
          )?.value;
          const prohibitedChars = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PROHIBITED_PASSWORD_CHAR
          )?.value;
          updatePasswordSettingsState({
            blacklist: blacklist,
            complexityRequirements: complexityRequirements,
            length: length,
            blacklistArray: blacklist.split(","),
            prohibitedChars: prohibitedChars,
          });
        }
      };
      fetchPasswordSettings();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickShowPassword = () => {
      updatePasswordDataState({ showPassword: !passwordData.showPassword });
    };
    const handleClickShowConfirmPassword = () => {
      updatePasswordDataState({ showConfirmPassword: !passwordData.showConfirmPassword });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {      
      const inputValue = e.target.value;
      if (inputValue.length > 0) {
        updateValidationErrorsState({
          passwordError: false,
          passwordErrorMessage: "",
        });
      } else {
        updateValidationErrorsState({
          passwordError: true,
          passwordErrorMessage: USERSIGNUPMESSAGES.PASSWORD_IS_REQUIRED,
        });
      }
      onPasswordInputChange(inputValue);
      return (
        validatePasswordByComplexity(inputValue) ||
        validatePasswordMatch(inputValue, passwordData.confirmPassword)
      );
    };
    const validatePasswordByComplexity = (password: string) => {
      const rules = parseRule(passwordSettings.complexityRequirements);
      const results = validatePasswordByRule(password, rules);
      updatePasswordDataState({ password: password, validationResults: results });
      return !Object.values(results).some((value) => value === false);
    };

    const validatePasswordMatch = (pass: string, confirmPass: string) => {
      let isValid = false;
      if (pass !== confirmPass && confirmPass.length > 0) {
        updateValidationErrorsState({
          confirmPasswordError: true,
          confirmPasswordErrorMessage: USERSIGNUPMESSAGES.PASSWORD_DO_NOT_MATCH,
        });
        isValid = false;
      } else {
        updateValidationErrorsState({
          confirmPasswordError: false,
          confirmPasswordErrorMessage: "",
        });
        isValid = true;
      }
      return isValid;
    };
    const handleConfirmPasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newPassword = e.target.value;
      if (newPassword.length > 0) {
        updateValidationErrorsState({
          confirmPasswordError: false,
          confirmPasswordErrorMessage: "",
        });
      } else {
        updateValidationErrorsState({
          confirmPasswordError: true,
          confirmPasswordErrorMessage: USERSIGNUPMESSAGES.CONFIRM_PASSWORD_IS_REQUIRED,
        });
      }
      updatePasswordDataState({ confirmPassword: newPassword });
      onConfirmPasswordInputChange(newPassword);
      validateConfirmPassword(newPassword);
    };

    const validateBothPassword = (password: string, confirmPassword: string) => {
      if (!password) {
        updateValidationErrorsState({
          passwordError: true,
          passwordErrorMessage: USERSIGNUPMESSAGES.PASSWORD_IS_REQUIRED,
        });
      }
      const passwordValid = validatePasswordByComplexity(password);
      const confirmValid = validateConfirmPassword(confirmPassword);
      if (!password || !passwordValid) {
        paswordRef.current?.focus();
      } else if (!confirmValid) {
        confirmPaswordRef.current?.focus();
      }
      return passwordValid && confirmValid;
    };
    const validateConfirmPassword = (confirmPass: string) => {
      const isValidLength = confirmPass?.length > 0;
      updateValidationErrorsState({
        confirmPasswordError: !isValidLength,
        confirmPasswordErrorMessage: isValidLength
          ? ""
          : USERSIGNUPMESSAGES.CONFIRM_PASSWORD_IS_REQUIRED,
      });
      if (isValidLength) {
        return validatePasswordMatch(passwordData.password, confirmPass);
      }
      return isValidLength;
    };

    const parseRule = (rule: string) => {
      const ruleRegex = /([ULNS])(\d+)/g;
      const requirements: Record<string, number> = {};
      let match;

      while ((match = ruleRegex.exec(rule)) !== null) {
        const [, type, count] = match;
        requirements[type] = parseInt(count, 10);
      }
      return requirements;
    };

    const validatePasswordByRule = (password: string, rules: PasswordComplexityRequirements) => {
      const results: PasswordComplexityRequirementsResult = {};
      if (rules.U) results.U = (password.match(/[A-Z]/g) || []).length >= rules.U;
      if (rules.L) results.L = (password.match(/[a-z]/g) || []).length >= rules.L;
      if (rules.N) results.N = (password.match(/[0-9]/g) || []).length >= rules.N;
      if (rules.S) results.S = (password.match(/[^A-Za-z0-9]/g) || []).length >= rules.S;
      if (passwordSettings?.length !== null) {
        results.Length = password.length >= passwordSettings.length;
      }
      if (userFirstName !== null || userLastName !== null) {
        results.UserDetailsContains = !validatePasswordContainsUserDetails(password);
      }
      if (password) {
        results.BlackList = validateRestrictedWords(password);
        const prohibitedRegex = createProhibitedRegex(passwordSettings.prohibitedChars);
        results.ProhibitedChars = !prohibitedRegex.test(password);
      }
      return results;
    };

    const validatePasswordContainsUserDetails = (password: string) => {
      if (
        [userFirstName, userLastName, userName]
          .filter((term) => term && term.trim()) // Remove empty or whitespace-only values
          .some((term) => password.toLowerCase().includes(term.toLowerCase()))
      ) {
        return true;
      }
      return false;
    };

    const validateRestrictedWords = (pass: string) => {
      for (const word of passwordSettings.blacklistArray) {
        const eachWord = String(word);
        if (pass.toLowerCase().includes(eachWord.toLowerCase())) {
          return false;
        }
      }
      return true;
    };
    const rules = parseRule(passwordSettings.complexityRequirements);
    const createProhibitedRegex = (chars: string) => {
      const arr = chars.split(",").map((c) => c.trim());
      const escaped = arr.map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      return new RegExp(`[${escaped.join("")}]`);
    };
    return (
      <>
        <Typography component="h1" variant="h1" className="paper-title">
          {title || "Set Yourself up with a Password"}
          <PopoverWithArrow
            icon={<HelpOutline className="helpoutline" />}
            htmlContent={POPOVERMESSAGES.SIGNIP_WITH_PASSWORD}
            placement="bottom"
            iconColor="#333333"
          />
        </Typography>
        <FormControl fullWidth>
          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type={passwordData.showPassword ? "text" : "password"}
            id="password"
            autoComplete="new-password"
            variant="outlined"
            error={validationErrors.passwordError}
            helperText={validationErrors.passwordErrorMessage}
            color={validationErrors.passwordError ? "error" : "primary"}
            value={passwordData.password}
            onChange={handlePasswordChange}
            inputRef={paswordRef}
            size="small"
            sx={{
              "& .MuiInputBase-root": { paddingRight: "6px" },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      passwordData.showPassword ? "hide the password" : "display the password"
                    }
                    onClick={handleClickShowPassword}
                    size="small"
                  >
                    {passwordData.showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {passwordData.password.length > 0 && (
            <List className="password-strength">
              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.U ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Uppercase Letters: ${rules.U}`}
                  sx={{
                    color: passwordData.validationResults.U ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.L ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Lowercase Letters: ${rules.L}`}
                  sx={{
                    color: passwordData.validationResults.U ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.N ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Numbers: ${rules.N}`}
                  sx={{
                    color: passwordData.validationResults.N ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.S ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Special Characters: ${rules.S}`}
                  sx={{
                    color: passwordData.validationResults.S ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.Length ? "success.main" : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Minimum length: ${passwordSettings.length}`}
                  sx={{
                    color: passwordData.validationResults.Length
                      ? "success.main"
                      : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.UserDetailsContains
                        ? "success.main"
                        : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Must not contain the First Name, Last Name, or Username"
                  sx={{
                    color: passwordData.validationResults.UserDetailsContains
                      ? "success.main"
                      : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.BlackList ? "success.main" : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Password must not contain - ${passwordSettings.blacklist.replace(/,/g, ", ")}`}
                  sx={{
                    color: passwordData.validationResults.BlackList
                      ? "success.main"
                      : "text.secondary",
                    wordWrap: "break-word",
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.ProhibitedChars
                        ? "success.main"
                        : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Not contain - ${passwordSettings.prohibitedChars}`}
                  sx={{
                    color: passwordData.validationResults.ProhibitedChars
                      ? "success.main"
                      : "text.secondary",
                    wordWrap: "break-word",
                  }}
                />
              </ListItem>
            </List>
          )}
        </FormControl>
        <FormControl fullWidth className="confirm-password-margin-top">
          <TextField
            required
            fullWidth
            name="password"
            label="Confirm Password"
            type={passwordData.showConfirmPassword ? "text" : "password"}
            id="password"
            autoComplete="new-password"
            variant="outlined"
            error={validationErrors.confirmPasswordError}
            helperText={validationErrors.confirmPasswordErrorMessage}
            color={validationErrors.confirmPasswordError ? "error" : "primary"}
            value={passwordData.confirmPassword}
            onChange={handleConfirmPasswordInputChange}
            size="small"
            inputRef={confirmPaswordRef}
            sx={{
              "& .MuiInputBase-root": { paddingRight: "6px" },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      passwordData.showPassword ? "hide the password" : "display the password"
                    }
                    onClick={handleClickShowConfirmPassword}
                  >
                    {passwordData.showConfirmPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </>
    );
  }
);

export default PasswordComponent;
